import { submitErrorReport } from '../services/errorReportApi';

const FRONTEND_SESSION_KEY = 'frontend-session-id';
const LISTENER_GUARD_KEY = '__frontsite_error_tracker_initialized__';

function createUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function parseHostname(value) {
  if (!value) return null;

  try {
    if (/^https?:\/\//i.test(value)) return new URL(value).hostname.toLowerCase();
    return new URL(`https://${value}`).hostname.toLowerCase();
  } catch (error) {
    return null;
  }
}

function inferEnvironmentFromHostname(hostname) {
  if (!hostname) return null;

  if (hostname === 'localhost' || hostname.endsWith('.local')) return 'development';
  if (hostname.includes('staging') || hostname.includes('stage')) return 'staging';
  if (hostname.includes('dev') || hostname.includes('development')) return 'development';
  if (hostname.includes('test') || hostname.includes('qa') || hostname.includes('uat')) return 'test';
  return 'production';
}

function getEnvironment() {
  const apiHostname = parseHostname(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL);
  const apiEnvironment = inferEnvironmentFromHostname(apiHostname);
  if (apiEnvironment) return apiEnvironment;

  if (typeof window !== 'undefined') {
    const appHostname = parseHostname(window.location.origin);
    const appEnvironment = inferEnvironmentFromHostname(appHostname);
    if (appEnvironment) return appEnvironment;
  }

  if (process.env.NODE_ENV === 'production') return 'production';
  if (process.env.NODE_ENV === 'test') return 'test';
  return 'development';
}

function getRoutePath() {
  if (typeof window === 'undefined') return '/';
  if (window.location.hash?.startsWith('#/')) return window.location.hash.slice(1);
  return window.location.pathname || '/';
}

function getFrontendSessionId() {
  try {
    const existing = localStorage.getItem(FRONTEND_SESSION_KEY);
    if (existing) return existing;
    const nextId = `fe-sess-${createUuid().slice(0, 12)}`;
    localStorage.setItem(FRONTEND_SESSION_KEY, nextId);
    return nextId;
  } catch (error) {
    return `fe-sess-${createUuid().slice(0, 12)}`;
  }
}

function buildPayload({
  errorType,
  message,
  code,
  stack,
  errorId = null,
  page = 'UnknownPage',
  action = 'unknown-action',
  practiceSessionId = null,
  http = {},
  recordingId = null,
}) {
  return {
    data: {
      'event-id': createUuid(),
      'error-id': errorId || null,
      timestamp: new Date().toISOString(),
      environment: getEnvironment(),
      app: {
        name: 'frontsite',
      },
      session: {
        'frontend-session-id': getFrontendSessionId(),
        'practice-session-id': practiceSessionId || null,
      },
      context: {
        route: getRoutePath(),
        page,
        action,
        'recording-id': recordingId || null,
      },
      error: {
        type: errorType,
        message: message || 'Unknown error',
        code,
        stack,
      },
      http: {
        method: http.method || null,
        url: http.url || null,
        'status-code': Object.prototype.hasOwnProperty.call(http, 'statusCode') ? http.statusCode : null,
        'timeout-ms': http.timeoutMs || null,
      },
    },
  };
}

export function trackErrorReport(params) {
  const payload = buildPayload(params);
  submitErrorReport(payload).catch(() => {});
}

export function trackAxiosError(error, context = {}) {
  const responseData = error?.response?.data;
  const httpStatus = error?.response?.status ?? null;
  const errorId = responseData?.['error-id'] || responseData?.errorId || null;
  const recordingId =
    context.recordingId ||
    error?.config?.meta?.recordingId ||
    null;

  trackErrorReport({
    errorType: 'axios',
    message: responseData?.message || error?.message || 'Axios request failed',
    code: error?.code || responseData?.code,
    stack: error?.stack,
    errorId,
    page: context.page || 'UnknownPage',
    action: context.action || 'api-request',
    practiceSessionId: context.practiceSessionId || null,
    recordingId,
    http: {
      method: context.method || error?.config?.method?.toUpperCase(),
      url: context.url || error?.config?.url,
      statusCode: httpStatus,
      timeoutMs: context.timeoutMs || error?.config?.timeout,
    },
  });
}

export function setupGlobalErrorTracking() {
  if (typeof window === 'undefined') return () => {};
  if (window[LISTENER_GUARD_KEY]) return () => {};
  window[LISTENER_GUARD_KEY] = true;
  const blockedInteractionCooldown = new Map();

  const onRuntimeError = (event) => {
    const runtimeError = event?.error;
    trackErrorReport({
      errorType: 'runtime',
      message: event?.message || runtimeError?.message || 'Unhandled runtime error',
      code: runtimeError?.name,
      stack: runtimeError?.stack,
      page: 'Global',
      action: 'window-error',
    });
  };

  const onUnhandledRejection = (event) => {
    const reason = event?.reason;
    trackErrorReport({
      errorType: 'runtime',
      message: reason?.message || String(reason || 'Unhandled promise rejection'),
      code: reason?.name,
      stack: reason?.stack,
      page: 'Global',
      action: 'unhandled-rejection',
    });
  };

  const onPointerDown = (event) => {
    const targetElement = event?.target;
    if (!targetElement || typeof targetElement.closest !== 'function') return;

    const interactiveElement = targetElement.closest('button, [role="button"], a, [data-track-action]');
    if (!interactiveElement) return;

    const isDisabled =
      interactiveElement.disabled === true ||
      interactiveElement.getAttribute('aria-disabled') === 'true';
    if (!isDisabled) return;

    const elementKey =
      interactiveElement.dataset?.trackAction ||
      interactiveElement.id ||
      interactiveElement.getAttribute('name') ||
      interactiveElement.className ||
      interactiveElement.tagName;

    const now = Date.now();
    const lastReportedAt = blockedInteractionCooldown.get(elementKey) || 0;
    if (now - lastReportedAt < 3000) return;
    blockedInteractionCooldown.set(elementKey, now);

    const actionLabel =
      interactiveElement.dataset?.trackAction ||
      interactiveElement.getAttribute('aria-label') ||
      interactiveElement.tagName.toLowerCase();

    trackErrorReport({
      errorType: 'ui',
      message: `Blocked interaction detected on ${actionLabel}`,
      code: 'ui-interaction-blocked',
      page: 'Global',
      action: 'blocked-click',
    });
  };

  window.addEventListener('error', onRuntimeError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
  window.addEventListener('pointerdown', onPointerDown, true);

  return () => {
    window.removeEventListener('error', onRuntimeError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
    window.removeEventListener('pointerdown', onPointerDown, true);
    window[LISTENER_GUARD_KEY] = false;
  };
}
